import Head from "next/head";

export default function Privacy() {
  return (
    <>
      <Head>
        <title>Privacy Policy — Rover by Raha Holding Ltd.</title>
        <meta
          property="og:title"
          content="Privacy Policy — Rover by Raha Holding Ltd."
          key="title"
        />
        <link rel="canonical" href="https://www.shopraha.com/privacy" />
      </Head>
      <div className="max-w-3xl mx-auto px-4 py-10 font-Regular text-darkish-purple">
        <h1 className="text-3xl font-Bold mb-2">Privacy Policy</h1>
        <p className="text-sm opacity-70 mb-8">
          Effective date: April 1, 2026
        </p>

        <p className="mb-6">
          Raha Holding Ltd. (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;),
          based in Abu Dhabi, UAE, operates <strong>Rover</strong> — a
          last-mile delivery driver companion app. This policy explains what
          data we collect, why we collect it, and how we handle it.
        </p>

        <Section title="1. Data We Collect">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Precise location (GPS)</strong> — Collected while the app
              is in use to enable route management and real-time delivery
              tracking.
            </li>
            <li>
              <strong>Phone number</strong> — Used for driver authentication via
              one-time password (OTP).
            </li>
            <li>
              <strong>Photos</strong> — Captured for proof-of-delivery
              documentation.
            </li>
            <li>
              <strong>Crash logs</strong> — Collected automatically via Sentry
              to monitor app stability and fix errors.
            </li>
          </ul>
        </Section>

        <Section title="2. Purpose of Data Collection">
          <p>
            All data is collected strictly for app functionality — route
            management, delivery verification, driver authentication, and error
            monitoring. We do not use your data for advertising or profiling.
          </p>
        </Section>

        <Section title="3. Data Sharing">
          <p>
            We do not sell or share your personal data with third parties. Data
            is used internally for delivery operations only.
          </p>
        </Section>

        <Section title="4. Data Retention">
          <p>
            Your data is retained while your driver account is active. Upon
            account termination, your data is deleted from our systems.
          </p>
        </Section>

        <Section title="5. Data Deletion">
          <p>
            You may request deletion of your personal data at any time by
            contacting us at{" "}
            <a
              href="mailto:support@btr.tech"
              className="underline text-dark-moderate-magenta"
            >
              support@btr.tech
            </a>
            . We will process your request within 30 days. Upon deletion:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Your account and associated personal data will be removed.</li>
            <li>
              Delivery records may be retained in anonymized form for
              operational reporting.
            </li>
          </ul>
        </Section>

        <Section title="6. Security">
          <p>
            We implement industry-standard measures to protect your data from
            unauthorized access, alteration, or destruction.
          </p>
        </Section>

        <Section title="7. Changes to This Policy">
          <p>
            We may update this policy from time to time. Changes will be
            reflected on this page with an updated effective date.
          </p>
        </Section>

        <Section title="8. Contact Us">
          <p>
            For questions about this privacy policy or to request data deletion,
            contact us at{" "}
            <a
              href="mailto:support@btr.tech"
              className="underline text-dark-moderate-magenta"
            >
              support@btr.tech
            </a>
            .
          </p>
          <p className="mt-2">
            Raha Holding Ltd.
            <br />
            Abu Dhabi, UAE
          </p>
        </Section>
      </div>
    </>
  );
}

function Section({title, children}) {
  return (
    <section className="mb-6">
      <h2 className="text-xl font-Bold mb-2">{title}</h2>
      {children}
    </section>
  );
}
